/* PlatformQualityWidget — iter100.164
 * ---------------------------------------------------------------
 * A compact "portal health at a glance" panel for the Admin/CoE
 * Operations Overview page. Auto-refreshes every 30 seconds so an
 * on-call operator can leave the page open and watch activity
 * without hitting F5.
 *
 * Metrics surfaced:
 *   • Active doctors (last 24h · via presence heartbeat)
 *   • Active team (admin + CoE staff, last 24h)
 *   • New cases (last 24h)
 *   • Open escalations (last 24h)
 *   • New patients (last 24h)
 *   • DB latency (synthetic single-query ping)
 *
 * Each tile carries a colour-tinted icon + tabular-nums value so the
 * strip stays visually stable when numbers change (no CLS).
 * ---------------------------------------------------------------
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import {
    Stethoscope, ShieldCheck, ClipboardText, Warning, UserPlus,
    Lightning,
} from "@phosphor-icons/react";
import AdminSectionShell from "@/components/admin/AdminSectionShell";

const REFRESH_MS = 30_000;

function relativeTime(iso) {
    if (!iso) return "";
    const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (diff < 5) return "just now";
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
}

/* Latency badge — colour is a hint at how the Mongo ping went.
 *   < 30ms   → green ("healthy")
 *   < 100ms  → amber ("watch")
 *   ≥ 100ms  → rose  ("slow")
 *   -1       → grey  ("unknown")
 */
function latencyTone(ms) {
    if (ms == null || ms < 0) return { text: "text-muted-foreground", bg: "bg-secondary", label: "n/a" };
    if (ms < 30)  return { text: "text-emerald-700", bg: "bg-emerald-50", label: "healthy" };
    if (ms < 100) return { text: "text-amber-700",   bg: "bg-amber-50",   label: "watch" };
    return { text: "text-rose-700", bg: "bg-rose-50", label: "slow" };
}

function Tile({ icon: Icon, label, value, sub, tone = "primary", testId }) {
    const paletteMap = {
        primary: { chip: "bg-primary/10 text-primary",         val: "text-foreground" },
        teal:    { chip: "bg-teal-50 text-teal-700",           val: "text-foreground" },
        amber:   { chip: "bg-amber-50 text-amber-700",         val: "text-amber-800" },
        rose:    { chip: "bg-rose-50 text-rose-700",           val: "text-rose-700" },
        slate:   { chip: "bg-slate-100 text-slate-600",        val: "text-foreground" },
        emerald: { chip: "bg-emerald-50 text-emerald-700",     val: "text-emerald-700" },
    };
    const p = paletteMap[tone] || paletteMap.primary;
    return (
        <div className="bg-card border border-border rounded-2xl p-4" data-testid={testId}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
                <span className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${p.chip}`}>
                    <Icon size={14} weight="duotone" />
                </span>
            </div>
            <div className={`font-display text-2xl font-bold tabular-nums leading-none ${p.val}`}>
                {value == null
                    ? <span className="inline-block w-10 h-6 bg-secondary rounded-md animate-pulse align-middle" />
                    : value}
            </div>
            {sub && <div className="text-[11px] text-muted-foreground mt-1.5">{sub}</div>}
        </div>
    );
}

export default function PlatformQualityWidget() {
    const [m, setM] = useState(null);
    const [err, setErr] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const timerRef = useRef(null);

    const load = useCallback(async () => {
        setRefreshing(true);
        try {
            const { data } = await api.get("/admin/platform-metrics");
            setM(data);
            setErr(null);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Unable to load platform metrics.");
        } finally {
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        load();
        timerRef.current = setInterval(load, REFRESH_MS);
        return () => timerRef.current && clearInterval(timerRef.current);
    }, [load]);

    const lat = useMemo(() => latencyTone(m?.db_latency_ms), [m?.db_latency_ms]);

    return (
        <AdminSectionShell
            eyebrow="Portal quality · last 24h"
            tone="teal"
            description="Platform health snapshot — auto-refreshes every 30 seconds."
            right={
                /* iter100.245 — Manual Refresh button removed. Modern
                   dashboards trust silent auto-refresh (already running
                   every 30s here). A subtle "Updated Xs ago" chip is
                   enough context. Full re-hydrate still available via
                   browser reload (Cmd/Ctrl-R). */
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground tabular-nums">
                    {m?.generated_at
                        ? `Updated ${relativeTime(m.generated_at)}`
                        : refreshing ? "Loading…" : "—"}
                </span>
            }
            bodyPad
            testid="platform-quality-widget"
        >
            {err ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="platform-quality-error">
                    <Warning size={14} weight="fill" className="inline-block mr-1.5 -mt-0.5" />
                    {err}
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <Tile icon={Stethoscope} label="Active doctors"   value={m?.active_doctors_24h}   sub="Logged in / heartbeat" tone="teal"    testId="pq-active-doctors" />
                    <Tile icon={ShieldCheck} label="Active team"      value={m?.active_admins_24h}    sub="Admin + CoE staff"    tone="primary" testId="pq-active-admins" />
                    <Tile icon={ClipboardText} label="New cases"      value={m?.new_cases_24h}        sub="Submitted in 24h"      tone="emerald" testId="pq-new-cases" />
                    <Tile icon={Warning} label="Escalations"          value={m?.escalations_open_24h} sub="Opened & still open"   tone={m?.escalations_open_24h > 0 ? "rose" : "slate"} testId="pq-escalations" />
                    <Tile icon={UserPlus} label="New patients"        value={m?.new_patients_24h}     sub="Signed up in 24h"      tone="amber"   testId="pq-new-patients" />
                    <Tile
                        icon={Lightning}
                        label="DB latency"
                        value={m?.db_latency_ms != null && m.db_latency_ms >= 0 ? `${m.db_latency_ms} ms` : (m ? "n/a" : null)}
                        sub={
                            m ? (
                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${lat.bg} ${lat.text} font-semibold text-[10px] uppercase tracking-wider`}>
                                    {lat.label}
                                </span>
                            ) : "Mongo ping"
                        }
                        tone="slate"
                        testId="pq-db-latency"
                    />
                </div>
            )}
        </AdminSectionShell>
    );
}
